<template>
  <!-- the padding-top overrides the padding-top set in libs/satellite/src/styles/main.scss -->
  <v-form ref="form" data-testid="yard-task-move-form" style="padding-top: 0 !important">
    <div class="d-flex flex-column gap-4">
      <div class="move-task-details">
        <div class="typography-heading-md-bold">
          {{ t('yard.drawers.createTaskDrawer.headers.moveDetails') }}
        </div>

        <yard-tasks-create-task-move-details
          @change="handleChange"
          :asset="currentTask.asset"
          :originSpot="currentTask.originSpot"
          :destinationSpot="currentTask.destinationSpot"
          :disabledFields="disabledFields" />
      </div>

      <v-expansion-panels class="d-flex flex-column gap-4" variant="accordion" multiple flat>
        <v-expansion-panel class="bordered-panel" data-testid="yard-task-move-description-panel">
          <v-expansion-panel-title>
            {{ t('yard.drawers.createTaskDrawer.headers.description') }}
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <yard-tasks-create-task-move-description
              @change="handleChange"
              @requestValidate="handleValidate"
              :description="currentTask.description"
              :disabledFields="disabledFields" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel
          class="bordered-panel"
          data-testid="yard-task-assignment-and-priority-panel">
          <v-expansion-panel-title>
            {{ t('yard.drawers.createTaskDrawer.headers.assignmentAndPriority') }}
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <yard-tasks-create-task-assignment
              @change="handleChange"
              :assignee="currentTask.assignee"
              :dueByDate="currentTask.dueByDate"
              :dueByTime="currentTask.dueByTime"
              :tags="currentTask.tags"
              :disabledFields="disabledFields" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel class="bordered-panel" data-testid="yard-task-attachments-panel">
          <v-expansion-panel-title>
            {{ t('yard.drawers.createTaskDrawer.headers.attachments') }}
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <div>Attachments</div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </v-form>
</template>

<script>
import { defineComponent, ref, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { isFormValid } from '@satellite/plugins/util.js';
import { mapTags } from '@satellite/helpers/yardTaskUtils';

import YardTasksCreateTaskMoveDetails from './YardTasksCreateTaskMoveDetails.vue';
import YardTasksCreateTaskMoveDescription from './YardTasksCreateTaskMoveDescription.vue';
import YardTasksCreateTaskAssignment from './YardTasksCreateTaskAssignment.vue';

import { validateTask } from './YardTasksCreateMoveTaskForm.helpers';

export default defineComponent({
  name: 'YardTasksCreateMoveTaskForm',
  components: {
    YardTasksCreateTaskMoveDetails,
    YardTasksCreateTaskMoveDescription,
    YardTasksCreateTaskAssignment
  },
  props: {
    modelValue: {
      type: Object,
      required: false,
      default: null,
      validator: value => {
        return !value || value?.type === 'move';
      }
    },
    disabledFields: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  emits: ['update:modelValue', 'validate'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const form = ref(null);

    const initializeTask = task => ({
      ...task,
      type: 'move',
      asset: task?.asset || null,
      originSpot: task?.originSpot || null,
      destinationSpot: task?.destinationSpot || null,
      description: task?.description || '',
      assignee: task?.assignee || null,
      dueByDate: task?.dueByDate || null,
      dueByTime: task?.dueByTime || null,
      tags: mapTags(task?.tags)
    });

    const currentTask = ref(null);

    // Watch for external changes to modelValue (e.g., when editing an existing task)
    watch(
      () => props.modelValue,
      newValue => {
        currentTask.value = initializeTask(newValue);

        /**
         * Validate after reactive updates have propagated
         * Since validateTask is pure and doesn't need DOM, nextTick isn't strictly necessary,
         * but it ensures any child component updates are complete before validation
         */
        nextTick(() => handleValidate({ runSilently: true }));
      },
      { deep: true, immediate: true }
    );

    /**
     * @param options - options for the validation
     * @param options.runSilently - if true, the form validation will not run, thus, error messages will not be shown
     */
    const handleValidate = async (options = { runSilently: false }) => {
      /**
       * The validation mechanisms here are equivalent because they are based on the same validation building blocks.
       * The difference lies in whether we want the validation to generate feedback at the field level (using the `isFormValid` function)
       * or at the form level (using the `validateTask` function), depending on whether we are only interested in determining
       * if the form submit button should be enabled or disabled.
       */
      if (!options.runSilently) {
        await isFormValid(form.value);
      }

      emit('validate', validateTask(currentTask.value));
    };

    const handleChange = eventData => {
      currentTask.value = {
        ...currentTask.value,
        ...eventData
      };

      emit('update:modelValue', currentTask.value);

      nextTick(() => handleValidate({ runSilently: true }));
    };

    return {
      t,
      form,
      currentTask,
      handleChange,
      handleValidate
    };
  }
});
</script>

<style lang="scss" scoped>
.move-task-details {
  display: flex;
  flex-direction: column;
  padding: $spacing-6;
  gap: $spacing-5;

  background-color: $color-neutral-10;
  border: 1px solid $color-line-border;
  border-radius: 4px;
}

.v-expansion-panel-title {
  @extend .typography-heading-md-bold;
}

.v-expansion-panel-text {
  border-top: 1px solid $color-line-border;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: $spacing-4 $spacing-6;
}

.bordered-panel {
  border: 1px solid $color-line-border;
  border-radius: $default-border-radius;
}
</style>

<style lang="scss">
// this is a revert for the margin-top that is set **globally** in the satellite library
.v-application form .v-input,
.v-overlay form .v-input {
  margin-top: 0 !important;
}
</style>
